import { useEffect, useState } from 'react'

import { t } from '../../../../../../common/i18n.js'
import type { Language } from '../../../../../../common/types/i18n.js'
import { Button } from '../../../../../components/button/button.js'
import { portalDelete, portalGet } from '../../../utils/api.js'
import './integrations-tab.css'

/**
 * Self-only API-keys panel (rendered inside the "Seaded" tab). Connects the
 * user's personal integrations:
 *  - Notion: a personal integration token, write-only (the server only ever
 *    reports whether one is saved, never the value). Powers the "Notion taskid"
 *    dashboard card.
 *  - Harvest: the OAuth connection shared with the BalanceTime feature. Here we
 *    only surface connect (redirect to Harvest) / disconnect — never a token.
 */
export const IntegrationsTab = ({ lang }: { lang: Language }) => {
    const tr = t.portal.profile.integrations

    // ── Harvest (OAuth connect/disconnect) ──
    const [harvestConnected, setHarvestConnected] = useState<boolean | null>(null)
    const [harvestAuthUrl, setHarvestAuthUrl] = useState<string | null>(null)
    const [harvestBusy, setHarvestBusy] = useState(false)

    // ── Google Drive (OAuth connect/disconnect; backend shared with /me/documents) ──
    const [driveStatus, setDriveStatus] = useState<{
        connected: boolean
        needsReconnect: boolean
    } | null>(null)
    const [driveConnectUrl, setDriveConnectUrl] = useState<string | null>(null)
    const [driveBusy, setDriveBusy] = useState(false)

    // ── Google Calendar (OoO sync for approved time-away) ──
    const [calendarStatus, setCalendarStatus] = useState<{
        connected: boolean
        needsReconnect: boolean
    } | null>(null)
    const [calendarConnectUrl, setCalendarConnectUrl] = useState<string | null>(null)
    const [calendarBusy, setCalendarBusy] = useState(false)

    useEffect(() => {
        let cancelled = false
        void (async () => {
            const res = await portalGet<{ connected: boolean; authUrl: string | null }>(
                '/api/portal/me/harvest',
            )
            if (!cancelled && res.ok) {
                setHarvestConnected(res.data.connected)
                setHarvestAuthUrl(res.data.authUrl)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [])

    useEffect(() => {
        let cancelled = false
        void (async () => {
            const res = await portalGet<{
                connected: boolean
                needsReconnect: boolean
                connectUrl: string
            }>('/api/portal/drive/status')
            if (!cancelled && res.ok) {
                setDriveStatus({
                    connected: res.data.connected,
                    needsReconnect: res.data.needsReconnect,
                })
                setDriveConnectUrl(res.data.connectUrl)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [])

    useEffect(() => {
        let cancelled = false
        void (async () => {
            const res = await portalGet<{
                connected: boolean
                needsReconnect: boolean
                connectUrl: string
            }>('/api/portal/calendar/status')
            if (!cancelled && res.ok) {
                setCalendarStatus({
                    connected: res.data.connected,
                    needsReconnect: res.data.needsReconnect,
                })
                setCalendarConnectUrl(res.data.connectUrl)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [])

    const handleHarvestDisconnect = async () => {
        setHarvestBusy(true)
        const res = await portalDelete('/api/portal/me/harvest')
        if (res.ok) {
            // Re-read to pick up a fresh authorize URL for re-connecting.
            const status = await portalGet<{ connected: boolean; authUrl: string | null }>(
                '/api/portal/me/harvest',
            )
            if (status.ok) {
                setHarvestConnected(status.data.connected)
                setHarvestAuthUrl(status.data.authUrl)
            } else {
                setHarvestConnected(false)
            }
        }
        setHarvestBusy(false)
    }

    const handleDriveConnect = () => {
        window.location.href = driveConnectUrl ?? '/auth/google/drive'
    }

    const handleDriveDisconnect = async () => {
        setDriveBusy(true)
        const res = await portalDelete('/api/portal/drive/disconnect')
        if (res.ok) {
            const status = await portalGet<{
                connected: boolean
                needsReconnect: boolean
                connectUrl: string
            }>('/api/portal/drive/status')
            if (status.ok) {
                setDriveStatus({
                    connected: status.data.connected,
                    needsReconnect: status.data.needsReconnect,
                })
                setDriveConnectUrl(status.data.connectUrl)
            } else {
                setDriveStatus({ connected: false, needsReconnect: false })
            }
        }
        setDriveBusy(false)
    }

    const handleCalendarConnect = () => {
        window.location.href = calendarConnectUrl ?? '/auth/google/calendar'
    }

    const handleCalendarDisconnect = async () => {
        setCalendarBusy(true)
        const res = await portalDelete('/api/portal/calendar/disconnect')
        if (res.ok) {
            const status = await portalGet<{
                connected: boolean
                needsReconnect: boolean
                connectUrl: string
            }>('/api/portal/calendar/status')
            if (status.ok) {
                setCalendarStatus({
                    connected: status.data.connected,
                    needsReconnect: status.data.needsReconnect,
                })
                setCalendarConnectUrl(status.data.connectUrl)
            } else {
                setCalendarStatus({ connected: false, needsReconnect: false })
            }
        }
        setCalendarBusy(false)
    }

    return (
        <div className="portal-integrations">
            {/* Harvest */}
            <div className="portal-integrations__group">
                <h3 className="portal-integrations__title">{tr.harvestTitle(lang)}</h3>
                <p className="portal-integrations__desc">{tr.harvestDescription(lang)}</p>

                {harvestConnected === null ? (
                    <p className="portal-integrations__desc">{tr.loading(lang)}</p>
                ) : harvestConnected ? (
                    <div className="portal-integrations__row">
                        <span className="portal-integrations__status">{tr.connected(lang)}</span>
                        <Button
                            variant="tertiary"
                            size="small"
                            text={tr.disconnect(lang)}
                            isDisabled={harvestBusy}
                            onClick={handleHarvestDisconnect}
                        />
                    </div>
                ) : (
                    <div className="portal-integrations__row">
                        <Button
                            variant="primary"
                            size="small"
                            text={tr.connectHarvest(lang)}
                            isDisabled={harvestBusy || !harvestAuthUrl}
                            onClick={() => {
                                if (harvestAuthUrl) window.location.href = harvestAuthUrl
                            }}
                        />
                    </div>
                )}
            </div>

            {/* Google Drive */}
            <div className="portal-integrations__group">
                <h3 className="portal-integrations__title">{tr.driveTitle(lang)}</h3>
                <p className="portal-integrations__desc">{tr.driveDescription(lang)}</p>

                {driveStatus === null ? (
                    <p className="portal-integrations__desc">{tr.loading(lang)}</p>
                ) : driveStatus.connected ? (
                    <div className="portal-integrations__row">
                        <span className="portal-integrations__status">{tr.connected(lang)}</span>
                        <Button
                            variant="tertiary"
                            size="small"
                            text={tr.disconnect(lang)}
                            isDisabled={driveBusy}
                            onClick={handleDriveDisconnect}
                        />
                    </div>
                ) : (
                    <div className="portal-integrations__row">
                        <Button
                            variant="primary"
                            size="small"
                            text={
                                driveStatus.needsReconnect
                                    ? tr.reconnectDrive(lang)
                                    : tr.connectDrive(lang)
                            }
                            isDisabled={driveBusy}
                            onClick={handleDriveConnect}
                        />
                    </div>
                )}
            </div>

            {/* Google Calendar */}
            <div className="portal-integrations__group">
                <h3 className="portal-integrations__title">{tr.calendarTitle(lang)}</h3>
                <p className="portal-integrations__desc">{tr.calendarDescription(lang)}</p>

                {calendarStatus === null ? (
                    <p className="portal-integrations__desc">{tr.loading(lang)}</p>
                ) : calendarStatus.connected ? (
                    <div className="portal-integrations__row">
                        <span className="portal-integrations__status">{tr.connected(lang)}</span>
                        <Button
                            variant="tertiary"
                            size="small"
                            text={tr.disconnect(lang)}
                            isDisabled={calendarBusy}
                            onClick={handleCalendarDisconnect}
                        />
                    </div>
                ) : (
                    <div className="portal-integrations__row">
                        <Button
                            variant="primary"
                            size="small"
                            text={
                                calendarStatus.needsReconnect
                                    ? tr.reconnectCalendar(lang)
                                    : tr.connectCalendar(lang)
                            }
                            isDisabled={calendarBusy}
                            onClick={handleCalendarConnect}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}
